import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, ListItemText, Menu, MenuItem, Popover, Snackbar, TextField, Tooltip,
    Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import EventIcon from '@mui/icons-material/Event';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    getTransitionStates, transitionWork, getNotes, addNote, editNote, getPriorState,
    currentWorkLinks, subscribeWorkLinks, loadWorkLinks, loadLinkCandidates,
    linkWork, unlinkWork, workKey, setWorkPeople, getStaffDirectory, getAdoAssignees, getMondayPeople,
} from '../../services/workService';
import {
    RELATIONS, getMeetings, linkWorkToMeeting, meetingTime,
} from '../../services/meetingService';
import { isClosed, isWithdrawn, fmtDate, SourceDot, StatusChip, SOURCE_LABEL } from './workUi';
import { Tip, Explain } from './explain';
import X from './explainText';
import PersonField from './PersonField';
import EditableNote from './EditableNote';
import ClientOwnerField from './ClientOwnerField';
import { useClientOwner, setClientOwner } from '../../services/clientOwners';
import { TodoEditDialog } from '../CS/ClientOverview/TodoForm';
import { useEmailReader } from './EmailReader';

/**
 * The five (now six) things you can do to a work item from its row, in
 * whichever system it lives in: link it to other work, link it to a meeting,
 * post a note, set its people, resolve it or reopen it.
 *
 * Two things this deliberately does not do:
 *
 * It does not decide what "resolved" means. The four systems disagree — DevOps
 * has two terminal states that mean different things, and a request's two are
 * opposites (Completed is delivered, Cancelled is withdrawn) — so the states
 * are read from each system's own schema and offered as a choice. Picking one
 * silently would be putting words in the team's mouth, and collapsing
 * Completed and Cancelled would inflate what was actually delivered.
 *
 * It does not assume where a reopened item belongs. The state it was resolved
 * from is remembered and offered first, marked as where it was — but work
 * moves on, and something resolved out of Final Review may deserve to come
 * back to Ready for Exploration. The reader chooses; the remembered state is a
 * default, not a destiny.
 *
 * Every change is confirmed first, naming the item and the exact move. A menu
 * alone was not enough: these rows are dense, they are also links, and picking
 * a menu item committed immediately against a system other people work in. That
 * is not hypothetical — it resolved a live Janesville ticket during testing,
 * from a click meant only to open the menu. The item's own title and the
 * "Final Review → Done This Week" line are there so a misaimed click is caught
 * by reading, not by regret.
 */

const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

const iconButtonSx = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '2px', p: 0.4, borderRadius: '4px',
    cursor: 'pointer', flexShrink: 0,
    color: active ? '#1d4ed8' : 'var(--color-text-secondary)',
    '&:hover': { background: 'rgba(127,127,127,0.16)', color: '#1d4ed8' },
});

/** One row-action button: icon, hover title, explain text, click. */
function ActionButton({ title, explain, onClick, active = false, hoverColor, children, testId }) {
    return (
        <Tip title={title} explain={explain}>
            <Box component="span" role="button" tabIndex={0} onClick={onClick} data-testid={testId}
                aria-label={title}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); }}
                sx={{ ...iconButtonSx(active),
                    ...(hoverColor ? { '&:hover': { background: 'rgba(127,127,127,0.16)', color: hoverColor } } : {}) }}>
                {children}
            </Box>
        </Tip>
    );
}

/**
 * Link this item to a meeting, from the item's own row.
 *
 * The meeting page could already do this, and that was not enough: somebody
 * looking at a ticket and thinking "this came out of the Hutto call" should
 * not have to go and find the Hutto call. The join is the same one either
 * way — it just has two doors now.
 */
function LinkToMeeting({ item, onChanged }) {
    const [anchor, setAnchor] = useState(null);
    const [meetings, setMeetings] = useState(null);
    const [pick, setPick] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const open = async (e) => {
        stop(e);
        setAnchor(e.currentTarget);
        if (meetings) return;
        try {
            const d = await getMeetings(item.clientId || '');
            setMeetings(d.meetings || []);
        } catch (err) { setError(err.message); }
    };

    const go = async (relation) => {
        setBusy(true);
        try {
            await linkWorkToMeeting(pick.id, {
                source: item.source, item_id: item.id, relation,
            });
            setAnchor(null); setPick(null);
            if (onChanged) onChanged();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    return (
        <>
            <ActionButton title="Link to a meeting" explain={X.actMeeting} onClick={open}>
                <EventIcon sx={{ fontSize: 15 }} />
            </ActionButton>
            <Menu anchorEl={anchor} open={!!anchor} onClick={stop}
                onClose={() => { setAnchor(null); setPick(null); }}
                slotProps={{ paper: { sx: { minWidth: 330, maxHeight: 420 } } }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.5, pt: 1, pb: 0.5 }}>
                    {pick ? 'How does it relate?' : 'Which meeting?'}
                </Typography>
                {error && <Alert severity="error" sx={{ m: 1, fontSize: 11 }}>{error}</Alert>}
                {!meetings && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <CircularProgress size={16} />
                    </Box>
                )}
                {meetings && !meetings.length && (
                    <Typography sx={{ fontSize: 11.5, px: 1.5, pb: 1,
                        color: 'var(--color-text-secondary)' }}>
                        No meetings captured for this client yet.
                    </Typography>
                )}
                {meetings && !pick && meetings.slice(0, 25).map((m) => (
                    <MenuItem key={m.id} onClick={() => setPick(m)} sx={{ display: 'block', py: 0.6 }}>
                        <Typography sx={{ fontSize: 12.5, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                            {m.subject}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--color-text-secondary)' }}>
                            {meetingTime(m.start_utc)}
                        </Typography>
                    </MenuItem>
                ))}
                {pick && RELATIONS.map((r) => (
                    <MenuItem key={r.key} disabled={busy} onClick={() => go(r.key)}
                        sx={{ display: 'block', py: 0.6 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{r.label}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--color-text-secondary)' }}>
                            {r.hint}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/**
 * Post and read notes on this item — the update log owners use to keep a
 * work item's state legible without opening its own system.
 *
 * Notes load lazily, on open, same as LinkToMeeting's meeting list: eagerly
 * fetching them for every row in a long list would mean four systems' worth
 * of extra round trips for something most rows are never opened to see.
 */
/** Read an email thread here, in the same reader the Email lane opens
 *  (Work/EmailReader.js; the row title opens the same reader). */
function ReadEmail({ item, onChanged }) {
    const { open, loading, host } = useEmailReader(item, onChanged);
    return (
        <>
            <ActionButton title={loading ? 'Reading…' : 'Read the thread here'} explain={X.emailVerbs} onClick={open}>
                <DraftsOutlinedIcon sx={{ fontSize: 15 }} />
            </ActionButton>
            {host}
        </>
    );
}


function AddNote({ item }) {
    const [anchor, setAnchor] = useState(null);
    const [notes, setNotes] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const open = async (e) => {
        stop(e);
        setAnchor(e.currentTarget);
        if (notes) return;
        try {
            setNotes(await getNotes(item));
        } catch (err) { setError(err.message); }
    };

    const post = async () => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setBusy(true);
        setError(null);
        try {
            setNotes(await addNote(item, trimmed));
            setText('');
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    return (
        <>
            <ActionButton title="Update — post a note" explain={X.actNote} onClick={open}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
            </ActionButton>
            {/* A Popover, not a Menu: MUI's Menu listens for letter keys to
                move focus between items (type-ahead), so text typed into a
                field inside it never reaches the field. Found live on
                2026-09-22 -- an update typed here silently vanished. */}
            <Popover anchorEl={anchor} open={!!anchor} onClick={stop}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 340, maxWidth: 380, maxHeight: 460 } } }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.5, pt: 1, pb: 0.5 }}>
                    Updates
                </Typography>
                {error && <Alert severity="error" sx={{ m: 1, fontSize: 11 }}>{error}</Alert>}
                <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 0.75 }}>
                    <TextField
                        size="small" fullWidth multiline maxRows={4} placeholder="Post an update…"
                        value={text} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); post(); } }}
                        sx={{ '& textarea': { fontSize: 12.5 } }}
                    />
                    <Button size="small" variant="contained" disabled={busy || !text.trim()} onClick={post}
                        sx={{ textTransform: 'none', flexShrink: 0 }}>
                        Post
                    </Button>
                </Box>
                <Divider />
                {!notes && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <CircularProgress size={16} />
                    </Box>
                )}
                {notes && !notes.length && (
                    <Typography sx={{ fontSize: 11.5, px: 1.5, py: 1,
                        color: 'var(--color-text-secondary)' }}>
                        No updates yet.
                    </Typography>
                )}
                {notes && notes.map((n) => (
                    <Box key={n.id || `${n.at}-${n.by}`} sx={{ px: 1.5, py: 0.75 }}>
                        <EditableNote note={n} source={item.source} textSx={{ fontSize: 12 }}
                            metaSx={{ fontSize: 10.5 }}
                            onSave={async (t) => setNotes(await editNote(item, n.id, t))}
                            byline={(
                                <Typography sx={{ fontSize: 10.5, color: 'var(--color-text-secondary)' }}>
                                    {n.by || 'unknown'} · {fmtDate(n.at)}
                                </Typography>
                            )} />
                    </Box>
                ))}
                <Box sx={{ pb: 0.5 }} />
            </Popover>
        </>
    );
}

/**
 * Set owner, requested by and stakeholders from the row.
 *
 * Owner options depend on the system: Azure DevOps validates System.AssignedTo
 * against its own org roster, so that list is offered for ADO items (the
 * Nexus staff directory is a different population, and offering it lost six
 * tickets on 2026-09-22); Monday's owner is one of two labels; to-dos and
 * requests take any directory email or free text. Requestor and stakeholders
 * use the Nexus staff directory everywhere.
 */
function SetPeople({ item, onChanged }) {
    const [anchor, setAnchor] = useState(null);
    const [staff, setStaff] = useState([]);
    const [adoRoster, setAdoRoster] = useState([]);
    const [mondayPeople, setMondayPeople] = useState([]);
    const [owner, setOwner] = useState(item.owner || '');
    const [requestedBy, setRequestedBy] = useState(item.requestor || '');
    const [stakeholders, setStakeholders] = useState(item.stakeholders || []);
    // Client owner (district), #4320 — every source, from the ledger.
    const district = useClientOwner(item);
    const [clientOwner, setClientOwnerDraft] = useState(district.value);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const open = (e) => {
        stop(e);
        setAnchor(e.currentTarget);
        setError(null);
        setOwner(item.owner || '');
        setRequestedBy(item.requestor || '');
        setStakeholders(item.stakeholders || []);
        setClientOwnerDraft(district.value);
        getStaffDirectory().then(setStaff);
        if (item.source === 'ado') getAdoAssignees().then(setAdoRoster);
        if (item.source === 'monday') getMondayPeople().then(setMondayPeople);
    };
    const close = () => setAnchor(null);

    const staffEmails = useMemo(() => staff.map((s) => s.email), [staff]);
    const mondayEmails = useMemo(() => mondayPeople.map((p) => p.email), [mondayPeople]);
    const peopleList = useMemo(() => [...staff, ...adoRoster, ...mondayPeople], [staff, adoRoster, mondayPeople]);
    // Internal accounts are always suggested; the ADO owner list is the ADO
    // org roster (ADO refuses anyone else); Monday's Owner and Stakeholders
    // are People columns, so they can only hold someone with a Monday seat.
    const ownerOptions = item.source === 'monday' ? (mondayEmails.length ? mondayEmails : staffEmails)
        : item.source === 'ado' && adoRoster.length ? adoRoster.map((a) => a.email)
            : staffEmails;
    const stakeholderOptions = item.source === 'monday' && mondayEmails.length ? mondayEmails : staffEmails;
    // Plain words here: every user sees these. The field names live in the
    // explain-mode hover (X.actPeople), not under the box.
    const ownerHint = item.source === 'ado'
        ? (adoRoster.length ? 'Only people in Azure DevOps can own a ticket.'
            : 'The DevOps list could not be loaded; DevOps will check the name when you save.')
        : item.source === 'monday' ? 'Only people with a Monday seat can own an outreach item.'
            : 'Anyone: pick from the list or type a name.';

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await setWorkPeople(item, {
                owner: owner.trim(),
                requestedBy: requestedBy.trim(),
                stakeholders: stakeholders.map((s) => String(s).trim()).filter(Boolean),
            });
            // Its own write (the ledger, mirrored to DevOps/Monday as a
            // comment), only when it changed and only when it could be read.
            if (district.status === 'ready' && clientOwner.trim() !== district.value) {
                await setClientOwner(item, clientOwner.trim());
            }
            close();
            if (onChanged) onChanged();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    const missing = !item.owner || !item.requestor || !(item.stakeholders || []).length;

    return (
        <>
            <ActionButton
                title={missing ? 'Assign — owner, requested by or stakeholders missing'
                    : 'Assign — owner, requested by, stakeholders'}
                explain={X.actPeople} onClick={open}
                active={false} hoverColor="#1d4ed8">
                <PersonOutlineIcon sx={{ fontSize: 15, color: missing ? '#b45309' : undefined }} />
            </ActionButton>
            <Popover anchorEl={anchor} open={!!anchor} onClose={close} onClick={stop}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 400, p: 1.5 } } }}>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    {SOURCE_LABEL[item.source] || item.source}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.title}>
                    {item.title}
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 1, fontSize: 11 }}>{error}</Alert>}

                <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.25 }}>Owner</Typography>
                <PersonField value={owner} onChange={(v) => setOwner(v || '')} options={ownerOptions}
                    people={peopleList} placeholder="Nobody" helperText={ownerHint}
                    FormHelperTextProps={{ sx: { fontSize: 10.5, mx: 0 } }} sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.25 }}>Requested by</Typography>
                <PersonField value={requestedBy} onChange={(v) => setRequestedBy(v || '')} options={staffEmails}
                    people={peopleList} placeholder="Nobody recorded" sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.25 }}>Stakeholders</Typography>
                <PersonField multiple value={stakeholders} onChange={setStakeholders} options={stakeholderOptions}
                    people={peopleList} placeholder="Name, then Enter"
                    helperText="Emailed if the item misses its update cadence."
                    FormHelperTextProps={{ sx: { fontSize: 10.5, mx: 0 } }} sx={{ mb: 1 }} />
                <ClientOwnerField clientId={(item.clientIds || []).length === 1 ? item.clientIds[0] : null}
                    value={clientOwner} onChange={setClientOwnerDraft}
                    unreadable={district.status === 'error'} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                    <Button size="small" disabled={busy} onClick={close} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button size="small" variant="contained" disabled={busy} onClick={save}
                        sx={{ textTransform: 'none' }}>
                        {busy ? 'Saving…' : `Save to ${item.source === 'ado' ? 'Azure DevOps' : item.source === 'monday' ? 'Monday'
                            : item.source === 'todo' ? 'Client Overview' : 'Implementations'}`}
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

/**
 * Edit a to-do from its row (#4319): to-dos only — DevOps, Monday and
 * requests are edited in their own systems. Opens TodoEditor, the same editor
 * as the to-do modal's Edit and the CS lists' pencil, client included.
 */
function EditTodo({ item, onChanged }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState(null);
    return (
        <>
            <ActionButton title="Edit to-do" explain={X.actEdit} testId="edit-todo"
                onClick={(e) => { stop(e); setOpen(true); }}>
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </ActionButton>
            {/* The dialog is portalled, but React events still bubble to the
                row: stop them here (propagation only — preventDefault would
                break the form's own radios and checkboxes). */}
            <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'contents' }}>
                {open && (
                    <TodoEditDialog todoId={item.id} clientName={item.clientTitle}
                        onClose={() => setOpen(false)}
                        onSaved={(r) => {
                            setOpen(false);
                            setNote({ severity: r.warnings.length ? 'warning' : 'success',
                                text: r.warnings.length ? `Saved. ${r.warnings.join(' ')}`
                                    : r.moved ? 'Saved — moved to the other client.' : 'Saved.' });
                            if (onChanged) onChanged();
                        }} />
                )}
            </Box>
            <Snackbar open={!!note} autoHideDuration={6000} onClose={() => setNote(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {note ? <Alert severity={note.severity} onClose={() => setNote(null)} sx={{ fontSize: 12 }}>{note.text}</Alert> : <span />}
            </Snackbar>
        </>
    );
}

/** The shared link map, fetched once for the whole page and kept current. */
function useWorkLinks() {
    const [state, setState] = useState(currentWorkLinks());
    useEffect(() => {
        const off = subscribeWorkLinks(setState);
        loadWorkLinks().catch(() => {});
        return off;
    }, []);
    return state;
}

/**
 * Link this item to another one -- in any of the five systems.
 *
 * DevOps can link tickets to tickets and Monday can link items to items, and
 * neither can point at the other, or at a client to-do, or at an
 * implementation request. The real chains cross all four (an outreach item
 * spawns a ticket, a to-do waits on a request), so the link is ours.
 *
 * A relation has a direction, and which way round it goes is the whole
 * meaning -- so both readings are offered as sentences about THIS item ("this
 * blocks that", "this is blocked by that") rather than asking the reader to
 * work out which end they are standing on.
 */
function LinkToWork({ item, onChanged }) {
    const { byItem, relations } = useWorkLinks();
    const links = useMemo(() => byItem[workKey(item)] || [], [byItem, item]);
    const [anchor, setAnchor] = useState(null);
    const [candidates, setCandidates] = useState(null);
    const [q, setQ] = useState('');
    const [pick, setPick] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const open = (e) => {
        stop(e);
        setAnchor(e.currentTarget);
        setError(null);
        if (!candidates) {
            loadLinkCandidates().then(setCandidates).catch((err) => setError(err.message));
        }
    };
    const close = () => { setAnchor(null); setPick(null); setQ(''); };

    const byKey = useMemo(
        () => Object.fromEntries((candidates || []).map((c) => [workKey(c), c])), [candidates]);
    const linkedKeys = useMemo(
        () => new Set(links.map((l) => `${l.other.source}:${l.other.id}`)), [links]);

    const matches = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return (candidates || [])
            .filter((c) => workKey(c) !== workKey(item) && !linkedKeys.has(workKey(c)))
            .filter((c) => !needle
                || `${c.title} ${c.clientTitle || ''} ${c.id} ${c.type || ''}`.toLowerCase().includes(needle))
            // Same client first, open work before closed: the likeliest partners.
            .sort((a, b) => (
                (Number(b.clientId === item.clientId && !!item.clientId)
                    - Number(a.clientId === item.clientId && !!item.clientId))
                || (Number(isClosed(a)) - Number(isClosed(b)))))
            .slice(0, 8);
    }, [candidates, q, item, linkedKeys]);

    // Every relation, both ways round, as a sentence about this item.
    const choices = useMemo(() => relations.flatMap((r) => (
        r.label === r.inverse
            ? [{ key: r.key, dir: 'out', text: r.label, hint: r.hint }]
            : [{ key: r.key, dir: 'out', text: r.label, hint: r.hint },
                { key: r.key, dir: 'in', text: r.inverse, hint: '' }])), [relations]);

    const save = async (choice) => {
        setBusy(true);
        setError(null);
        try {
            if (choice.dir === 'out') await linkWork(item, pick, choice.key);
            else await linkWork(pick, item, choice.key);
            setPick(null); setQ('');
            if (onChanged) onChanged();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    const remove = async (l) => {
        setBusy(true);
        setError(null);
        try {
            await unlinkWork(item, l.other);
            if (onChanged) onChanged();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    const tip = links.length
        ? `${links.length} linked item${links.length === 1 ? '' : 's'} — view or link another`
        : 'Link this to another work item';

    return (
        <>
            <ActionButton title={tip} explain={X.actLink} onClick={open} active={links.length > 0}>
                <LinkIcon sx={{ fontSize: 15 }} />
                {links.length > 0 && (
                    <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700 }}>
                        {links.length}
                    </Typography>
                )}
            </ActionButton>
            <Popover anchorEl={anchor} open={!!anchor} onClose={close} onClick={stop}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 440, maxHeight: 520, p: 1.5 } } }}>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    {SOURCE_LABEL[item.source] || item.source}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.title}>
                    {item.title}
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 1, fontSize: 11 }}>{error}</Alert>}

                {!pick && (
                    <>
                        {links.length === 0 && (
                            <Typography sx={{ fontSize: 11.5, mb: 1,
                                color: 'var(--color-text-secondary)' }}>
                                Not linked to anything yet.
                            </Typography>
                        )}
                        {links.map((l) => {
                            const other = byKey[`${l.other.source}:${l.other.id}`];
                            const url = other?.url || l.other.url;
                            return (
                                <Box key={`${l.other.source}:${l.other.id}`} sx={{ py: 0.6,
                                    borderTop: '1px solid var(--color-border, rgba(127,127,127,0.15))' }}>
                                    {/* Title on its own line: beside five buttons and a status
                                        chip it was squeezed down to a single word. */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, flexShrink: 0,
                                            color: 'var(--color-text-secondary)' }}>
                                            {l.label}
                                        </Typography>
                                        <SourceDot source={l.other.source} />
                                        <Typography sx={{ fontSize: 12.5, flex: 1, minWidth: 0,
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap' }}
                                            title={other?.title || l.other.title || ''}>
                                            {other?.title || l.other.title
                                                || `${SOURCE_LABEL[l.other.source] || l.other.source} ${l.other.id}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                        <Typography sx={{ fontSize: 10.5, flex: 1, minWidth: 0,
                                            color: 'var(--color-text-secondary)', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {[other?.clientTitle, SOURCE_LABEL[l.other.source],
                                                `linked by ${(l.linked_by || 'unknown').split('@')[0]}`,
                                                fmtDate(l.linked_at_utc)].filter(Boolean).join(' · ')}
                                        </Typography>
                                        {other && <StatusChip item={other} />}
                                        {url && (
                                            <Tooltip title="Open it">
                                                <Box component="a" href={url} target="_blank" rel="noreferrer"
                                                    sx={{ ...iconButtonSx(false), textDecoration: 'none' }}>
                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                        {/* The linked item is a work item too, so it gets the
                                            same buttons -- minus this one, or it never ends. */}
                                        {other && <WorkActions item={other} onChanged={onChanged} nested />}
                                        <Tooltip title="Unlink these two">
                                            <Box component="span" role="button" tabIndex={0}
                                                onClick={() => !busy && remove(l)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') remove(l); }}
                                                sx={iconButtonSx(false)}>
                                                <LinkOffIcon sx={{ fontSize: 14 }} />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}

                        <Divider sx={{ my: 1 }} />
                        <TextField size="small" fullWidth autoFocus value={q}
                            placeholder="Link to… search tickets, Monday items, to-dos, requests"
                            onChange={(e) => setQ(e.target.value)}
                            sx={{ '& input': { fontSize: 12.5 } }} />
                        {!candidates && !error && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                <CircularProgress size={16} />
                            </Box>
                        )}
                        {candidates && matches.length === 0 && (
                            <Typography sx={{ fontSize: 11.5, py: 1,
                                color: 'var(--color-text-secondary)' }}>
                                Nothing matches “{q}”.
                            </Typography>
                        )}
                        {matches.map((c) => (
                            <Box key={workKey(c)} role="button" tabIndex={0}
                                onClick={() => setPick(c)}
                                onKeyDown={(e) => { if (e.key === 'Enter') setPick(c); }}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 0.5,
                                    borderRadius: '4px', cursor: 'pointer',
                                    '&:hover': { background: 'rgba(127,127,127,0.12)' } }}>
                                <SourceDot source={c.source} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 12.5, overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {c.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10.5,
                                        color: 'var(--color-text-secondary)' }}>
                                        {[c.clientTitle || c.clientLabel, SOURCE_LABEL[c.source],
                                            c.source === 'ado' ? `#${c.id}` : null]
                                            .filter(Boolean).join(' · ')}
                                    </Typography>
                                </Box>
                                <StatusChip item={c} />
                            </Box>
                        ))}
                    </>
                )}

                {pick && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Box component="span" role="button" tabIndex={0}
                                onClick={() => setPick(null)}
                                onKeyDown={(e) => { if (e.key === 'Enter') setPick(null); }}
                                sx={iconButtonSx(false)}>
                                <ArrowBackIcon sx={{ fontSize: 15 }} />
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>This item…</Typography>
                        </Box>
                        {choices.map((c) => (
                            <Box key={`${c.key}-${c.dir}`} role="button" tabIndex={0}
                                onClick={() => !busy && save(c)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) save(c); }}
                                sx={{ py: 0.5, px: 0.75, borderRadius: '4px', cursor: 'pointer',
                                    opacity: busy ? 0.5 : 1,
                                    '&:hover': { background: 'rgba(127,127,127,0.12)' } }}>
                                <Typography sx={{ fontSize: 12.5 }}>
                                    <b>{c.text}</b>{' '}
                                    <span style={{ color: 'var(--color-text-secondary)' }}>
                                        {pick.title.length > 44 ? `${pick.title.slice(0, 43)}…` : pick.title}
                                    </span>
                                </Typography>
                                {c.hint && (
                                    <Typography sx={{ fontSize: 10.5,
                                        color: 'var(--color-text-secondary)' }}>{c.hint}</Typography>
                                )}
                            </Box>
                        ))}
                        <Typography sx={{ fontSize: 10.5, mt: 1, color: 'var(--color-text-secondary)' }}>
                            Kept in Nexus only — nothing is written to DevOps or Monday. Linking the
                            same two items again replaces how they relate.
                        </Typography>
                    </>
                )}
            </Popover>
        </>
    );
}

const WorkActions = ({ item, onChanged, nested = false }) => {
    const [anchor, setAnchor] = useState(null);
    const [states, setStates] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(null);
    const [pending, setPending] = useState(null);

    const closed = isClosed(item) || isWithdrawn(item);
    const action = closed ? 'reopen' : 'resolve';
    // To-dos and requests learn their pre-resolve state when the menu opens.
    const [fetchedPrior, setFetchedPrior] = useState(undefined);

    const open = async (e) => {
        stop(e);
        setAnchor(e.currentTarget);
        setError(null);
        if (closed && !item.priorState && !fetchedPrior && (item.source === 'todo' || item.source === 'request')) {
            getPriorState(item).then((p) => setFetchedPrior(p || null));
        }
        if (states) return;
        try {
            setStates(await getTransitionStates(item.source, item.type));
        } catch (err) {
            setError(`Could not load the states for ${item.source}: ${err.message}`);
        }
    };

    // Chosen, not yet done. The dialog is the commit point.
    const choose = (toState) => { setAnchor(null); setPending(toState); };

    const go = async (toState) => {
        setBusy(true);
        setError(null);
        try {
            const r = await transitionWork({ item, action, toState });
            setPending(null);
            setDone(`${item.title.slice(0, 48)}${item.title.length > 48 ? '…' : ''} — `
                + `${r.from_state || 'unknown'} → ${r.to_state}`);
            // Pages may mark the moment (Client Overview throws confetti for a
            // finished to-do, as its old checkbox did).
            if (action === 'resolve') {
                window.dispatchEvent(new CustomEvent('nexus:work-resolved', { detail: { item, toState: r.to_state } }));
            }
            if (onChanged) onChanged();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const resolveOpts = states?.resolve || [];
    const reopenOpts = states?.reopen || [];
    // Where it was, first and named. Everything else follows in the system's
    // own order so the list stays predictable between items.
    const prior = item.priorState || fetchedPrior || '';
    const ordered = prior
        ? [prior, ...reopenOpts.filter((s) => s !== prior)]
        : reopenOpts;

    // An email thread (#4338): Resolve writes the Outlook tag "Resolved" on the
    // thread and Reopen takes it off (Patrick, 2026-10-19); Link and Link to
    // meeting work as on any row. Update and Assign are not offered yet (the
    // thread's own notes and assignment are phase 2). Read opens the thread
    // here in the lane's reader; the envelope opens it in Outlook.
    const email = item.source === 'email';

    return (
        <>
            {!nested && <LinkToWork item={item} onChanged={onChanged} />}
            <LinkToMeeting item={item} onChanged={onChanged} />
            {!email && <AddNote item={item} />}
            {!email && <SetPeople item={item} onChanged={onChanged} />}
            {email && <ReadEmail item={item} onChanged={onChanged} />}
            {email && (
                <ActionButton title="Open the thread in Outlook" explain={X.emailVerbs}
                    onClick={(e) => { stop(e); const u = item.email?.webLink || item.url; if (u) window.open(u, '_blank', 'noopener'); }}>
                    <MailOutlineIcon sx={{ fontSize: 15 }} />
                </ActionButton>
            )}
            <ActionButton
                title={closed ? 'Reopen — you choose the state' : 'Resolve'}
                explain={closed ? X.actReopen : X.actResolve} onClick={open}
                hoverColor={closed ? '#b45309' : '#047857'}>
                {closed
                    ? <ReplayIcon sx={{ fontSize: 15 }} />
                    : <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            </ActionButton>
            {/* Edit (to-dos only), approved by Patrick 2026-10-12 (#4319):
                added after the frozen verbs, so none of them moves. */}
            {item.source === 'todo' && <EditTodo item={item} onChanged={onChanged} />}

            <Menu
                anchorEl={anchor} open={!!anchor} onClose={(e) => { stop(e || {}); setAnchor(null); }}
                onClick={stop}
                slotProps={{ paper: { sx: { minWidth: 268 } } }}
            >
                <Explain text={X.resolveMenuHead(item.source)} block>
                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                            {closed ? 'Reopen as' : 'Resolve as'}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--color-text-secondary)' }}>
                            {closed
                                ? 'Choose the state it should go back to'
                                : `This changes the item in ${item.source === 'ado' ? 'Azure DevOps'
                                    : item.source === 'monday' ? 'Monday' : item.source === 'email' ? 'Outlook' : 'its own system'}`}
                        </Typography>
                    </Box>
                </Explain>
                <Divider sx={{ my: 0.5 }} />

                {!states && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <CircularProgress size={16} />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ m: 1, fontSize: 11 }}>{error}</Alert>
                )}

                {states && !closed && resolveOpts.map((o) => (
                    <MenuItem key={o.state} disabled={busy} onClick={() => choose(o.state)}>
                        <ListItemText
                            primary={o.state}
                            secondary={o.outcome === 'withdrawn'
                                ? `${o.hint} — counted as withdrawn, not done`
                                : o.hint}
                            primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: 10.5 }}
                        />
                    </MenuItem>
                ))}
                {states && !closed && resolveOpts.length === 0 && (
                    <Typography sx={{ fontSize: 11.5, px: 1.5, py: 1,
                        color: 'var(--color-text-secondary)' }}>
                        This system exposes no state we can set from here.
                    </Typography>
                )}

                {states && closed && ordered.map((s) => (
                    <MenuItem key={s} disabled={busy} onClick={() => choose(s)}>
                        <ListItemText
                            primary={s}
                            secondary={s === prior ? 'where it was when it was resolved' : undefined}
                            primaryTypographyProps={{
                                fontSize: 12.5, fontWeight: s === prior ? 700 : 500,
                            }}
                            secondaryTypographyProps={{ fontSize: 10.5 }}
                        />
                    </MenuItem>
                ))}
                {states && closed && !prior && (
                    <Typography sx={{ fontSize: 10.5, px: 1.5, pb: 1, pt: 0.5,
                        color: 'var(--color-text-secondary)' }}>
                        This was not resolved from here, so there is no record of the state it came
                        from — pick where it belongs.
                    </Typography>
                )}
            </Menu>

            <Dialog open={!!pending} onClose={() => !busy && setPending(null)}
                onClick={stop} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15, pb: 0.5 }}>
                    {closed ? 'Reopen this item?' : 'Resolve this item?'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                        {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)', mb: 1.5 }}>
                        {item.clientTitle || item.clientLabel || 'No client'}
                        {item.source === 'ado' ? ` · DevOps #${item.id}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                        p: 1, borderRadius: '6px', background: 'rgba(127,127,127,0.1)' }}>
                        <Typography sx={{ fontSize: 12.5 }}>{item.status || 'unknown'}</Typography>
                        <Typography sx={{ fontSize: 12.5, opacity: 0.6 }}>→</Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{pending}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)', mt: 1.5 }}>
                        This changes the item in{' '}
                        {item.source === 'ado' ? 'Azure DevOps' : item.source === 'monday' ? 'Monday'
                            : item.source === 'email' ? 'Outlook (the thread\'s tag, in every mailbox it was seen in)'
                                : item.source === 'todo' ? 'Client Overview' : 'Implementations'}
                        , where other people will see it. The state it is in now is recorded, so
                        this can be put back.
                    </Typography>
                    {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 11 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button size="small" disabled={busy} onClick={() => setPending(null)}
                        sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" disabled={busy}
                        onClick={() => go(pending)} sx={{ textTransform: 'none' }}>
                        {busy ? 'Working…' : closed ? `Reopen as ${pending}` : `Resolve as ${pending}`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!done} autoHideDuration={5000} onClose={() => setDone(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setDone(null)} sx={{ fontSize: 12 }}>
                    {done}
                </Alert>
            </Snackbar>
        </>
    );
};

export default WorkActions;
